import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  addOrderComplements,
  fulfillPendingOrderExtras,
  removeOrderLines,
  resendTransferWhatsapp,
  assignOrderDriver,
  notifyDriverWhatsapp,
  deleteAdminOrder,
  getAdminOrderPaymentLink,
  getAdminOrderStats,
  getAdminOrders,
  getPizasUiConfig,
  getComplementMenu,
  getDeliveryDrivers,
  getMpPayments,
  confirmTransferProof,
  rejectTransferProof,
  fetchTransferProofImageUrl,
  getOrderTransferProof,
  linkMpPaymentToOrder,
  syncAdminOrderMercadoPago,
  syncMercadoPagoByOrderNumber,
  updateAdminOrder,
  formatApiDetail,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin } from '../lib/adminRoles';
import { Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { addMinutesToIso, formatTimeMexico, nowMexicoMs, todayMexicoYmd } from '../lib/dateMexico';
import OrderWeekDayPicker from '../components/OrderWeekDayPicker';
import OrderKitchenTimer from '../components/OrderKitchenTimer';
import { kitchenTimerStart, shouldShowKitchenTimer } from '../lib/kitchenTimer';
import { notifyOrdersChanged, subscribeOrdersChanged } from '../lib/orderSync';
import { orderSourceLabel } from '../lib/orderSourceLabel';
import CounterOrderDialog from '../components/CounterOrderDialog';
import CancelOrderDialog from '../components/CancelOrderDialog';
import OrderSummaryDialog from '../components/OrderSummaryDialog';
import { getOrderReceipt } from '../lib/api';
import { printOrderReceipt } from '../lib/orderReceiptPrint';
import PrinterSlotPanel from '../components/PrinterSlotPanel';
import { printEscPosLines, isPrinterSessionActive, warmReconnectPrinter } from '../lib/thermalPrint/webBluetoothPrinter';
import { buildReceiptTicketLines } from '../lib/thermalPrint/escPos';
import { getPairedPrinter } from '../lib/thermalPrint/printerSlots';

function orderMapsUrl(o) {
  const lat = o?.address_lat;
  const lng = o?.address_lng;
  if (lat != null && lng != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng))) {
    return `https://maps.google.com/maps?q=${lat},${lng}&z=17&hl=es`;
  }
  const addr = String(o?.delivery_address || '');
  const m = addr.match(/https?:\/\/(?:maps\.google\.com|www\.google\.com\/maps)[^\s]+/i);
  return m ? m[0] : null;
}

const PAYMENT_STATUS_LABELS = {
  paid: 'Pagado',
  unpaid: 'No pagado',
  awaiting_receipt: 'Pendiente comprobante',
  pending_verification: 'Comprobante por revisar',
  pay_on_delivery: 'Paga al entregar',
};

function isTransferAwaitingReceipt(o) {
  return (
    o?.payment_method === 'transfer' &&
    ['awaiting_receipt', 'unpaid'].includes(o?.payment_status) &&
    !o?.payment_proof_id
  );
}

function hasTransferProofToReview(o) {
  return (
    o?.payment_method === 'transfer' &&
    o?.payment_status !== 'paid' &&
    (Boolean(o?.payment_proof_id) || o?.payment_status === 'pending_verification')
  );
}

const PAYMENT_METHOD_LABELS = {
  mercadopago: 'Mercado Pago',
  transfer: 'Transferencia',
  cash_on_delivery: 'Contra entrega',
  store_cash: 'Efectivo en sucursal',
};

const STATUS_LABELS = {
  pending_payment: 'Pago pendiente',
  pending_review: 'Por revisar',
  proposed_delivery: 'Horario propuesto',
  accepted: 'Aceptado',
  preparing: 'En preparación',
  awaiting_extras: 'En caja (complementos)',
  ready: 'Listo',
  dispatched: 'En camino',
  delivered: 'Entregado',
  rejected: 'Rechazado',
  cancelled: 'Cancelado',
};

const CANCELLABLE_STATUSES = new Set([
  'pending_payment',
  'pending_review',
  'proposed_delivery',
  'accepted',
  'preparing',
  'awaiting_extras',
  'ready',
  'dispatched',
]);

function canStaffCancel(order) {
  return CANCELLABLE_STATUSES.has(order?.status);
}

const STATUS_CARD_ORDER = [
  'pending_payment',
  'pending_review',
  'accepted',
  'preparing',
  'awaiting_extras',
  'ready',
  'dispatched',
  'delivered',
  'rejected',
  'cancelled',
];

function orderStatusLabel(order) {
  if (order?.delivery_type === 'pickup' && order?.status === 'ready') {
    return 'Listo para recoger';
  }
  return STATUS_LABELS[order?.status] || order?.status || '';
}

function requiresPrepay(o) {
  return o.payment_method === 'mercadopago' || o.payment_method === 'transfer';
}

function canAcceptToKitchen(o) {
  if (o.payment_status === 'pending_verification') return false;
  if (isTransferAwaitingReceipt(o)) return false;
  if (requiresPrepay(o)) return o.payment_status === 'paid';
  return true;
}

const COUNTER_CATEGORY_HINTS = [
  'extra',
  'extras',
  'bebida',
  'bebidas',
  'complemento',
  'complementos',
  'postre',
  'refresco',
];

function lineIsCounterCategory(ln) {
  const cat = String(ln?.category_name || '').toLowerCase();
  return COUNTER_CATEGORY_HINTS.some((hint) => cat.includes(hint));
}

function orderRequiresKitchen(o) {
  if (o?.requires_kitchen === false) return false;
  if (o?.requires_kitchen === true) return true;
  const lines = (o?.lines || []).filter((ln) => String(ln?.line_type || 'item') !== 'extra');
  if (!lines.length) return true;
  return lines.some((ln) => !lineIsCounterCategory(ln));
}

function hasPendingCounterLines(o) {
  return Boolean(o?.has_pending_counter_lines);
}

function sumExtrasCartPrice(catalog, cart) {
  const items = (catalog || []).flatMap((c) => c.items || []);
  return (cart || []).reduce((sum, ln) => {
    const it = items.find((x) => x.id === ln.menu_item_id);
    return sum + (Number(it?.price) || 0) * (ln.qty || 1);
  }, 0);
}

function lineIsCashierRemovable(ln) {
  if (ln?.cashier_removable === true) return true;
  if (ln?.cashier_removable === false) return false;
  return !ln?.inventory_deducted;
}

function pendingIncludedLines(order) {
  return (order?.lines || []).filter(
    (ln) => lineIsCashierRemovable(ln) && !ln.inventory_deducted,
  );
}

function lockedOrderLines(order) {
  return (order?.lines || [])
    .map((ln, index) => ({ ln, index }))
    .filter(({ ln }) => !lineIsCashierRemovable(ln));
}

function removableOrderLines(order) {
  return (order?.lines || [])
    .map((ln, index) => ({ ln, index }))
    .filter(({ ln }) => lineIsCashierRemovable(ln));
}

function isDeliveryOrder(o) {
  return o?.delivery_type !== 'pickup';
}

function canPrintOrderTicket(o) {
  if (!o) return false;
  if (o.status === 'delivered') return true;
  if (o.delivery_type === 'pickup' && o.pickup_status === 'picked_up') return true;
  const paid = String(o.payment_status || '').toLowerCase() === 'paid';
  return paid && ['ready', 'dispatched'].includes(o.status);
}

function OrderEtaAdjust({ iso, onChange }) {
  const atMin = Date.parse(iso) <= nowMexicoMs() + 60_000;

  return (
    <div className="flex items-center justify-center gap-0.5">
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-7 w-7 shrink-0"
        disabled={atMin}
        title="Restar 10 minutos"
        onClick={() => {
          const next = addMinutesToIso(iso, -10);
          if (Date.parse(next) < nowMexicoMs()) {
            onChange(new Date(nowMexicoMs()).toISOString());
            toast.info('La hora no puede ser anterior a ahora');
          } else {
            onChange(next);
          }
        }}
      >
        <Minus className="h-3.5 w-3.5" />
      </Button>
      <span className="text-sm font-medium tabular-nums min-w-[5.5rem] text-center">
        {formatTimeMexico(iso)}
      </span>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-7 w-7 shrink-0"
        title="Sumar 10 minutos"
        onClick={() => onChange(addMinutesToIso(iso, 10))}
      >
        <Plus className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export default function AdminOrders() {
  const { user } = useAuth();
  const superAdmin = Boolean(user && isSuperAdmin(user.role));
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('');
  const [selectedDate, setSelectedDate] = useState(() => todayMexicoYmd());
  const [etaOverrides, setEtaOverrides] = useState({});
  const [clockTick, setClockTick] = useState(() => Date.now());
  const [syncOrderNumber, setSyncOrderNumber] = useState('');
  const [syncingId, setSyncingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [assignOrderId, setAssignOrderId] = useState(null);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [notifyingDriverId, setNotifyingDriverId] = useState(null);
  const [extrasOrder, setExtrasOrder] = useState(null);
  const [complementCatalog, setComplementCatalog] = useState([]);
  const [extrasCart, setExtrasCart] = useState([]);
  const [extrasLoading, setExtrasLoading] = useState(false);
  const [extrasSaving, setExtrasSaving] = useState(false);
  const [mpPayments, setMpPayments] = useState([]);
  const [linkPayment, setLinkPayment] = useState(null);
  const [linkOrderId, setLinkOrderId] = useState('');
  const [linkingPayment, setLinkingPayment] = useState(false);
  const [proofImageUrl, setProofImageUrl] = useState('');
  const [proofPreviewId, setProofPreviewId] = useState(null);
  const [proofActionId, setProofActionId] = useState(null);
  const [counterDialogOpen, setCounterDialogOpen] = useState(false);
  const [resendingTransferId, setResendingTransferId] = useState(null);
  const [cancelReasonOptions, setCancelReasonOptions] = useState([]);
  const [cancelOrder, setCancelOrder] = useState(null);
  const [cancellingOrder, setCancellingOrder] = useState(false);
  const [summaryOrder, setSummaryOrder] = useState(null);
  const [printingOrderId, setPrintingOrderId] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const data = await getAdminOrderStats(selectedDate);
      setStats(data);
    } catch {
      /* ignore */
    }
  }, [selectedDate]);

  const load = useCallback(async () => {
    try {
      const data = await getAdminOrders(filter || undefined, selectedDate);
      setOrders(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Error al cargar pedidos');
    }
  }, [filter, selectedDate]);

  const loadMpPayments = useCallback(async () => {
    try {
      const data = await getMpPayments(selectedDate, true);
      setMpPayments(Array.isArray(data) ? data : []);
    } catch {
      setMpPayments([]);
    }
  }, [selectedDate]);

  const loadDrivers = useCallback(async () => {
    try {
      const data = await getDeliveryDrivers(true);
      setDrivers(Array.isArray(data) ? data : []);
    } catch (err) {
      setDrivers([]);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudieron cargar repartidores');
    }
  }, []);

  useEffect(() => {
    getPizasUiConfig()
      .then((cfg) => {
        const list = Array.isArray(cfg?.order_cancellation_reasons)
          ? cfg.order_cancellation_reasons
          : [];
        setCancelReasonOptions(list);
      })
      .catch(() => setCancelReasonOptions([]));
  }, []);

  const confirmCancelOrder = async (reasons) => {
    if (!cancelOrder) return;
    setCancellingOrder(true);
    try {
      await updateAdminOrder(cancelOrder.id, {
        status: 'cancelled',
        cancellation_reasons: reasons,
      });
      notifyOrdersChanged();
      toast.success(`Pedido #${cancelOrder.order_number} cancelado`);
      setCancelOrder(null);
      await load();
      await loadStats();
    } catch (err) {
      toast.error(formatApiDetail(err?.response?.data?.detail, 'No se pudo cancelar el pedido'));
    } finally {
      setCancellingOrder(false);
    }
  };

  useEffect(() => {
    const refreshAll = () => {
      load();
      loadStats();
      loadMpPayments();
    };
    refreshAll();
    const unsub = subscribeOrdersChanged(refreshAll);
    const t = setInterval(refreshAll, 5000);
    return () => {
      unsub();
      clearInterval(t);
    };
  }, [load, loadStats, loadMpPayments]);

  useEffect(() => {
    const t = setInterval(() => setClockTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const act = async (id, payload) => {
    try {
      await updateAdminOrder(id, payload);
      notifyOrdersChanged();
      toast.success('Pedido actualizado');
      await load();
      await loadStats();
      await loadMpPayments();
    } catch (err) {
      toast.error(formatApiDetail(err?.response?.data?.detail, 'No se pudo actualizar'));
    }
  };

  const resolveProofIdForOrder = async (order) => {
    if (order?.payment_proof_id) return order.payment_proof_id;
    if (!order?.id) return null;
    try {
      const proof = await getOrderTransferProof(order.id);
      return proof?.id || null;
    } catch {
      return null;
    }
  };

  const openProofImage = async (proofId) => {
    try {
      if (proofImageUrl) URL.revokeObjectURL(proofImageUrl);
      const url = await fetchTransferProofImageUrl(proofId);
      setProofImageUrl(url);
      setProofPreviewId(proofId);
    } catch {
      toast.error('No se pudo cargar la imagen');
    }
  };

  const openProofImageForOrder = async (order) => {
    const proofId = await resolveProofIdForOrder(order);
    if (!proofId) {
      toast.error('Aún no hay comprobante vinculado a este pedido');
      return;
    }
    await openProofImage(proofId);
  };

  const confirmProofForOrder = async (order) => {
    const proofId = await resolveProofIdForOrder(order);
    if (!proofId) {
      toast.error('Aún no hay comprobante vinculado a este pedido');
      return;
    }
    await confirmProof(proofId, order?.order_number);
  };

  const rejectProofForOrder = async (order) => {
    const proofId = await resolveProofIdForOrder(order);
    if (!proofId) {
      toast.error('Aún no hay comprobante vinculado a este pedido');
      return;
    }
    await rejectProof(proofId);
  };

  const closeProofImage = () => {
    if (proofImageUrl) URL.revokeObjectURL(proofImageUrl);
    setProofImageUrl('');
    setProofPreviewId(null);
  };

  const confirmProof = async (proofId, orderNumber) => {
    try {
      setProofActionId(proofId);
      await confirmTransferProof(proofId);
      notifyOrdersChanged();
      const label = orderNumber ? `#${orderNumber}` : '';
      toast.success(`Pago confirmado${label ? ` — pedido ${label}` : ''} en cocina`);
      await load();
      await loadStats();
    } catch (err) {
      toast.error(formatApiDetail(err?.response?.data?.detail, 'No se pudo confirmar'));
    } finally {
      setProofActionId(null);
    }
  };

  const rejectProof = async (proofId) => {
    if (!window.confirm('¿Rechazar este comprobante? El cliente deberá enviar otro.')) return;
    try {
      setProofActionId(proofId);
      await rejectTransferProof(proofId);
      notifyOrdersChanged();
      toast.success('Comprobante rechazado');
      await load();
    } catch (err) {
      toast.error(formatApiDetail(err?.response?.data?.detail, 'No se pudo rechazar'));
    } finally {
      setProofActionId(null);
    }
  };

  const resendDriverWhatsapp = async (orderId) => {
    try {
      setNotifyingDriverId(orderId);
      await notifyDriverWhatsapp(orderId);
      toast.success('WhatsApp reenviado al repartidor');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo reenviar WhatsApp');
    } finally {
      setNotifyingDriverId(null);
    }
  };

  const resendTransferInstructions = async (orderId) => {
    try {
      setResendingTransferId(orderId);
      await resendTransferWhatsapp(orderId);
      toast.success('Datos de transferencia enviados por WhatsApp al cliente');
    } catch (err) {
      toast.error(formatApiDetail(err?.response?.data?.detail, 'No se pudo enviar WhatsApp'));
    } finally {
      setResendingTransferId(null);
    }
  };

  const confirmAssignDriver = async () => {
    if (!assignOrderId || !selectedDriverId) {
      toast.error('Selecciona un repartidor');
      return;
    }
    try {
      setAssigning(true);
      await assignOrderDriver(assignOrderId, selectedDriverId);
      toast.success('Repartidor asignado y notificado por WhatsApp');
      setAssignOrderId(null);
      setSelectedDriverId('');
      await load();
      await loadStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo asignar repartidor');
    } finally {
      setAssigning(false);
    }
  };

  const assignOrder = useMemo(
    () => orders.find((o) => o.id === assignOrderId) || null,
    [orders, assignOrderId],
  );

  const getOrderEtaIso = useCallback(
    (o) => etaOverrides[o.id] || o.estimated_ready_at || '',
    [etaOverrides],
  );

  const setOrderEta = (orderId, iso) => {
    setEtaOverrides((prev) => ({ ...prev, [orderId]: iso }));
  };

  const sortedOrders = useMemo(() => {
    void clockTick;
    const rank = (o) => {
      if (!shouldShowKitchenTimer(o)) return 2;
      const start = Date.parse(kitchenTimerStart(o) || '');
      return Number.isNaN(start) ? 1 : -start;
    };
    return [...orders].sort((a, b) => rank(a) - rank(b));
  }, [orders, clockTick]);

  const syncMp = async (orderId) => {
    try {
      setSyncingId(orderId);
      const data = await syncAdminOrderMercadoPago(orderId);
      toast.success(data?.message || 'Pago sincronizado con Mercado Pago');
      notifyOrdersChanged();
      await load();
      await loadStats();
      await loadMpPayments();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se encontró pago aprobado en Mercado Pago');
    } finally {
      setSyncingId(null);
    }
  };

  const syncMpByNumber = async () => {
    const num = syncOrderNumber.trim();
    if (!num) {
      toast.error('Escribe el número de pedido (ej. 0012)');
      return;
    }
    try {
      setSyncingId('by-number');
      const data = await syncMercadoPagoByOrderNumber(num);
      toast.success(data?.message || 'Pago sincronizado');
      setSyncOrderNumber('');
      await load();
      await loadStats();
      await loadMpPayments();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo sincronizar');
    } finally {
      setSyncingId(null);
    }
  };

  const removeOrder = async (o) => {
    const label = `#${o.order_number} — ${o.customer_name}`;
    if (
      !window.confirm(
        `¿Eliminar el pedido ${label}?\n\nEsta acción no se puede deshacer (solo super administrador).`,
      )
    ) {
      return;
    }
    try {
      setDeletingId(o.id);
      await deleteAdminOrder(o.id);
      toast.success(`Pedido ${o.order_number} eliminado`);
      await load();
      await loadStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo eliminar el pedido');
    } finally {
      setDeletingId(null);
    }
  };

  const copyPaymentLink = async (orderId) => {
    try {
      const data = await getAdminOrderPaymentLink(orderId);
      const url = data.payment_url || '';
      if (!url) {
        toast.error('No hay link de pago');
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success('Link de pago copiado');
    } catch {
      toast.error('No se pudo obtener el link');
    }
  };

  const zoneLabel = (o) => {
    if (o.delivery_type === 'pickup') return 'Sucursal';
    const km = Number(o.distance_km);
    const hasKm = Number.isFinite(km);
    if (o.within_delivery_radius === false) {
      return hasKm ? `Fuera (${km.toFixed(2)} km)` : 'Fuera';
    }
    return hasKm ? `OK (${km.toFixed(2)} km)` : 'OK';
  };

  const openAssignModal = async (orderId) => {
    await loadDrivers();
    setAssignOrderId(orderId);
    setSelectedDriverId('');
  };

  const printTicket = async (order) => {
    if (!order?.id) return;
    setPrintingOrderId(order.id);
    try {
      const data = await getOrderReceipt(order.id);
      let live = isPrinterSessionActive('cashier');
      if (!live && getPairedPrinter('cashier') && data?.receipt) {
        // Reconexion silenciosa (sin abrir selector: el gesto del clic ya se perdio tras el await)
        live = await warmReconnectPrinter('cashier').catch(() => false);
      }
      if (live && data?.receipt) {
        try {
          await printEscPosLines('cashier', buildReceiptTicketLines(data.receipt));
          toast.success('Ticket enviado a la impresora');
          return;
        } catch (btErr) {
          printOrderReceipt(data);
          toast.warning('Fallo la impresora Bluetooth; se abrio impresion del navegador. Pulsa Reconectar o Quitar y Emparejar.');
          return;
        }
      }
      printOrderReceipt(data);
      if (getPairedPrinter('cashier')) {
        toast.warning('Impresora Bluetooth no conectada: se abrio impresion del navegador. Pulsa Reconectar (o Quitar y Emparejar) junto a Bandeja de pedidos hasta ver BLE OK.');
      }
    } catch (err) {
      toast.error(err?.response ? formatApiDetail(err.response.data?.detail, 'No se pudo generar el ticket') : (err?.message || 'No se pudo imprimir el ticket'));
    } finally {
      setPrintingOrderId(null);
    }
  };

  const openExtrasDialog = async (order) => {
    setExtrasOrder(order);
    setExtrasCart([]);
    setExtrasLoading(true);
    try {
      const data = await getComplementMenu();
      setComplementCatalog(data.categories || []);
    } catch {
      toast.error('No se pudo cargar complementos');
      setComplementCatalog([]);
    } finally {
      setExtrasLoading(false);
    }
  };

  const addExtraLine = (item) => {
    setExtrasCart((prev) => {
      const found = prev.find((x) => x.menu_item_id === item.id);
      if (found) {
        return prev.map((x) =>
          x.menu_item_id === item.id ? { ...x, qty: x.qty + 1 } : x,
        );
      }
      return [...prev, { menu_item_id: item.id, qty: 1, notes: '' }];
    });
  };

  const changeExtraCartQty = (menuItemId, delta) => {
    setExtrasCart((prev) => {
      const next = prev
        .map((x) =>
          x.menu_item_id === menuItemId ? { ...x, qty: (x.qty || 1) + delta } : x,
        )
        .filter((x) => (x.qty || 0) > 0);
      return next;
    });
  };

  const removeOrderLineAt = async (lineIndex) => {
    if (!extrasOrder) return;
    setExtrasSaving(true);
    try {
      const updated = await removeOrderLines(extrasOrder.id, [lineIndex]);
      setExtrasOrder(updated);
      notifyOrdersChanged();
      const paid = String(updated.payment_status || '').toLowerCase() === 'paid';
      toast.success(
        paid
          ? `Ítem quitado. Nuevo total: $${Number(updated.total || 0).toFixed(2)} (revisa si hubo cobro de más)`
          : `Ítem quitado. Total: $${Number(updated.total || 0).toFixed(2)}`,
      );
      await load();
      await loadStats();
    } catch (err) {
      toast.error(formatApiDetail(err?.response?.data?.detail, 'No se pudo quitar el ítem'));
    } finally {
      setExtrasSaving(false);
    }
  };

  const extrasCartSubtotal = useMemo(
    () => sumExtrasCartPrice(complementCatalog, extrasCart),
    [complementCatalog, extrasCart],
  );

  const extrasPreviewTotal = useMemo(() => {
    if (!extrasOrder) return 0;
    return Number(extrasOrder.total || 0) + extrasCartSubtotal;
  }, [extrasOrder, extrasCartSubtotal]);

  const counterOnlySale = useMemo(
    () => Boolean(extrasOrder && !orderRequiresKitchen(extrasOrder)),
    [extrasOrder],
  );

  const confirmIncludedExtras = async () => {
    if (!extrasOrder) return;
    setExtrasSaving(true);
    try {
      const updated = await fulfillPendingOrderExtras(extrasOrder.id);
      setExtrasOrder(updated);
      notifyOrdersChanged();
      toast.success('Complementos incluidos registrados en caja');
      await load();
      await loadStats();
    } catch (err) {
      toast.error(formatApiDetail(err?.response?.data?.detail, 'No se pudieron confirmar complementos'));
    } finally {
      setExtrasSaving(false);
    }
  };

  const addExtrasToOrder = async () => {
    if (!extrasOrder || extrasCart.length === 0) return;
    setExtrasSaving(true);
    try {
      const updated = await addOrderComplements(extrasOrder.id, extrasCart);
      setExtrasOrder(updated);
      setExtrasCart([]);
      notifyOrdersChanged();
      toast.success(`Extras agregados. Total del pedido: $${Number(updated.total || 0).toFixed(2)}`);
      await load();
      await loadStats();
    } catch (err) {
      toast.error(formatApiDetail(err?.response?.data?.detail, 'No se pudieron agregar extras'));
    } finally {
      setExtrasSaving(false);
    }
  };

  const closeCashierSale = async ({ autoAddCart = false } = {}) => {
    if (!extrasOrder) return;
    if (extrasCart.length > 0 && !autoAddCart) {
      toast.error('Pulsa «Agregar extras al pedido» o quita los ítems del carrito antes de cerrar');
      return;
    }
    setExtrasSaving(true);
    try {
      let order = extrasOrder;
      if (extrasCart.length > 0) {
        order = await addOrderComplements(order.id, extrasCart);
        setExtrasCart([]);
      }
      if (order.status === 'awaiting_extras' || hasPendingCounterLines(order)) {
        order = await fulfillPendingOrderExtras(order.id);
        setExtrasOrder(order);
      }
      await updateAdminOrder(order.id, { status: 'ready' });
      notifyOrdersChanged();
      toast.success(`Venta cerrada — pedido #${order.order_number} listo`);
      setExtrasOrder(null);
      setExtrasCart([]);
      await load();
      await loadStats();
    } catch (err) {
      toast.error(formatApiDetail(err?.response?.data?.detail, 'No se pudo cerrar la venta'));
    } finally {
      setExtrasSaving(false);
    }
  };

  const reviewStatuses = new Set(['pending_review', 'proposed_delivery']);
  const paymentPendingStatuses = new Set(['pending_payment']);

  const pendingPaymentOrders = useMemo(
    () =>
      orders.filter(
        (o) => o.status === 'pending_payment' && o.payment_status !== 'paid' && o.payment_method === 'mercadopago',
      ),
    [orders],
  );

  const submitLinkPayment = async () => {
    if (!linkPayment || !linkOrderId) {
      toast.error('Elige un pedido');
      return;
    }
    setLinkingPayment(true);
    try {
      await linkMpPaymentToOrder(linkPayment.payment_id, linkOrderId);
      toast.success('Pago vinculado al pedido');
      setLinkPayment(null);
      setLinkOrderId('');
      await load();
      await loadStats();
      await loadMpPayments();
    } catch (err) {
      toast.error(formatApiDetail(err?.response?.data?.detail) || 'No se pudo vincular');
    } finally {
      setLinkingPayment(false);
    }
  };

  return (
    <div className="space-y-6">
      <OrderWeekDayPicker
        selectedYmd={selectedDate}
        onSelectYmd={setSelectedDate}
        className="-mt-2 mb-2"
      />

      {stats?.by_status ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {STATUS_CARD_ORDER.map((key) => {
            const count =
              key === 'pending_review'
                ? Number(stats.by_status.pending_review || 0) +
                  Number(stats.by_status.proposed_delivery || 0)
                : Number(stats.by_status[key] || 0);
            const active = filter === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(active ? '' : key)}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  active ? 'border-primary bg-primary/10' : 'bg-card hover:bg-muted/50'
                }`}
              >
                <div className="text-2xl font-bold tabular-nums">{count}</div>
                <div className="text-xs text-muted-foreground leading-tight mt-1">
                  {STATUS_LABELS[key]}
                </div>
              </button>
            );
          })}
        </div>
      ) : null}

      {mpPayments.length > 0 ? (
        <Card className="border-amber-300 bg-amber-50/50">
          <CardHeader>
            <CardTitle className="text-base">Pagos Mercado Pago sin vincular</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Cobros aprobados en MP que aún no están ligados a un pedido. Vincúlalos manualmente si el
              webhook no llegó (ej. pruebas en local sin ngrok).
            </p>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Hora</TableHead>
                    <TableHead>Monto</TableHead>
                    <TableHead>ID MP</TableHead>
                    <TableHead>Referencia</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mpPayments.map((p) => (
                    <TableRow key={p.payment_id}>
                      <TableCell className="text-xs whitespace-nowrap">
                        {p.created_at ? formatTimeMexico(p.created_at) : '—'}
                      </TableCell>
                      <TableCell>${Number(p.amount || 0).toFixed(2)}</TableCell>
                      <TableCell className="font-mono text-xs">{p.payment_id}</TableCell>
                      <TableCell className="text-xs max-w-[120px] truncate">{p.external_reference || '—'}</TableCell>
                      <TableCell>
                        <Button type="button" size="sm" variant="outline" onClick={() => setLinkPayment(p)}>
                          Vincular a pedido
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <CounterOrderDialog
        open={counterDialogOpen}
        onOpenChange={setCounterDialogOpen}
        onCreated={() => {
          load();
          loadStats();
        }}
      />

      <CancelOrderDialog
        open={Boolean(cancelOrder)}
        order={cancelOrder}
        reasonOptions={cancelReasonOptions}
        onOpenChange={(open) => {
          if (!open) setCancelOrder(null);
        }}
        onConfirm={confirmCancelOrder}
        saving={cancellingOrder}
      />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
          <CardTitle>Bandeja de pedidos</CardTitle>
          <PrinterSlotPanel slot="cashier" compact />
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-2 mb-4 p-3 rounded-md border bg-muted/30">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">¿Pagó en MP y no aparece? Sincronizar por número</Label>
              <Input
                placeholder="0012"
                value={syncOrderNumber}
                onChange={(e) => setSyncOrderNumber(e.target.value)}
                className="h-9 mt-1"
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={syncingId === 'by-number'}
              onClick={syncMpByNumber}
            >
              {syncingId === 'by-number' ? 'Buscando…' : 'Sincronizar pago MP'}
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Button variant={filter === '' ? 'default' : 'outline'} onClick={() => setFilter('')}>
              Todos
            </Button>
            <Button
              variant={filter === 'in_kitchen' ? 'default' : 'outline'}
              onClick={() => setFilter('in_kitchen')}
            >
              En cocina
            </Button>
            <Button
              type="button"
              className="ml-auto shrink-0"
              data-testid="counter-order-btn"
              onClick={() => setCounterDialogOpen(true)}
            >
              <Plus className="w-4 h-4 mr-1" />
              Pedido en sucursal
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>ETA</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Zona</TableHead>
                <TableHead>Pago</TableHead>
                <TableHead>Tiempo</TableHead>
                <TableHead>Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-10 text-muted-foreground">
                    No hay pedidos para mostrar. Los nuevos pedidos aparecerán aquí automáticamente.
                  </TableCell>
                </TableRow>
              ) : null}
              {sortedOrders.map((o) => (
                <TableRow key={o.id}>
                  <TableCell>{o.order_number}</TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{o.customer_name}</div>
                    <div className="text-xs text-muted-foreground">{o.customer_phone}</div>
                    <div className="text-[11px] text-muted-foreground">
                      Origen: {orderSourceLabel(o.source)}
                    </div>
                    {o.mercadopago_payment_id ? (
                      <div className="text-[11px] font-mono text-muted-foreground">
                        MP: {o.mercadopago_payment_id}
                      </div>
                    ) : null}
                    {o.assigned_driver_name ? (
                      <div className="text-[11px] text-primary">
                        Repartidor: {o.assigned_driver_name}
                        {o.status === 'dispatched' ? (
                          <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="h-auto p-0 ml-1 text-xs"
                            disabled={notifyingDriverId === o.id}
                            onClick={() => resendDriverWhatsapp(o.id)}
                          >
                            {notifyingDriverId === o.id ? 'Enviando…' : 'Reenviar WA'}
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                    <div className="text-xs truncate max-w-[220px]">{o.delivery_address}</div>
                    {o.taken_by_name ? (
                      <div className="text-[11px] text-muted-foreground">Tomó: {o.taken_by_name}</div>
                    ) : null}
                    {orderMapsUrl(o) ? (
                      <a
                        href={orderMapsUrl(o)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary underline"
                      >
                        Ver ubicación en mapa
                      </a>
                    ) : null}
                  </TableCell>
                  <TableCell>${Number(o.total).toFixed(0)}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{orderStatusLabel(o)}</Badge>
                    {o.status === 'cancelled' && Array.isArray(o.cancelled_reasons) && o.cancelled_reasons.length > 0 ? (
                      <p className="text-[11px] text-muted-foreground mt-1 max-w-[140px]">
                        {o.cancelled_reasons.join(' · ')}
                      </p>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-sm tabular-nums">
                    {reviewStatuses.has(o.status) && getOrderEtaIso(o) ? (
                      <OrderEtaAdjust
                        iso={getOrderEtaIso(o)}
                        onChange={(iso) => setOrderEta(o.id, iso)}
                      />
                    ) : (
                      formatTimeMexico(o.estimated_ready_at)
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{o.delivery_type === 'pickup' ? 'Recoger' : 'Domicilio'}</Badge>
                  </TableCell>
                  <TableCell>
                    {o.within_delivery_radius === false ? (
                      <span className="text-amber-700 text-xs">{zoneLabel(o)}</span>
                    ) : (
                      <span className="text-green-700 text-xs">{zoneLabel(o)}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{PAYMENT_STATUS_LABELS[o.payment_status] || o.payment_status}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {PAYMENT_METHOD_LABELS[o.payment_method] || o.payment_method}
                    </div>
                    {o.payment_method === 'cash_on_delivery' && reviewStatuses.has(o.status) ? (
                      <Badge variant="outline" className="mt-1 text-amber-800 border-amber-300 bg-amber-50">
                        {o.delivery_type === 'pickup'
                          ? orderRequiresKitchen(o)
                            ? 'Contra entrega — confirmar recoger'
                            : 'Contra entrega — confirmar en caja'
                          : orderRequiresKitchen(o)
                            ? 'Contra entrega — confirmar y enviar a cocina'
                            : 'Contra entrega — confirmar en caja (solo bebidas)'}
                      </Badge>
                    ) : null}
                    {paymentPendingStatuses.has(o.status) && o.payment_method === 'mercadopago' ? (
                      <Badge variant="outline" className="mt-1 text-amber-800 border-amber-300 bg-amber-50">
                        Esperando pago MP
                      </Badge>
                    ) : null}
                    {isTransferAwaitingReceipt(o) ? (
                      <Badge variant="outline" className="mt-1 text-amber-800 border-amber-300 bg-amber-50">
                        Esperando comprobante
                      </Badge>
                    ) : null}
                    {o.payment_method === 'transfer' && o.payment_status === 'pending_verification' ? (
                      <Badge variant="outline" className="mt-1 text-orange-800 border-orange-300 bg-orange-50">
                        Revisar comprobante
                      </Badge>
                    ) : null}
                    {requiresPrepay(o) && o.payment_status !== 'paid' && reviewStatuses.has(o.status) ? (
                      <Badge variant="outline" className="mt-1 text-red-800 border-red-200 bg-red-50">
                        Esperando confirmación de pago
                      </Badge>
                    ) : null}
                    {o.payment_status !== 'paid' && o.payment_method === 'mercadopago' ? (
                      <>
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="h-auto p-0 text-xs block"
                          onClick={() => copyPaymentLink(o.id)}
                        >
                          Copiar link de pago
                        </Button>
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="h-auto p-0 text-xs block text-primary"
                          disabled={syncingId === o.id}
                          onClick={() => syncMp(o.id)}
                        >
                          {syncingId === o.id ? 'Sincronizando…' : 'Sincronizar pago MP'}
                        </Button>
                      </>
                    ) : null}
                    {hasTransferProofToReview(o) ? (
                      <div className="flex flex-wrap gap-1 mt-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => openProofImageForOrder(o)}
                        >
                          Ver imagen
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          disabled={Boolean(proofActionId)}
                          onClick={() => confirmProofForOrder(o)}
                        >
                          {proofActionId ? '…' : 'Confirmar pago'}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="text-destructive h-8 px-2"
                          disabled={Boolean(proofActionId)}
                          onClick={() => rejectProofForOrder(o)}
                        >
                          Rechazar
                        </Button>
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <OrderKitchenTimer order={o} />
                  </TableCell>
                  <TableCell className="space-x-1 flex flex-wrap gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSummaryOrder(o)}
                    >
                      Ver resumen
                    </Button>
                    {canPrintOrderTicket(o) ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={printingOrderId === o.id}
                        onClick={() => printTicket(o)}
                      >
                        {printingOrderId === o.id ? '…' : 'Imprimir ticket'}
                      </Button>
                    ) : null}
                    {canStaffCancel(o) ? (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setCancelOrder(o)}
                      >
                        Cancelar
                      </Button>
                    ) : null}
                    {paymentPendingStatuses.has(o.status) ? (
                      <>
                        {o.payment_method === 'transfer' ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={resendingTransferId === o.id}
                            onClick={() => resendTransferInstructions(o.id)}
                          >
                            {resendingTransferId === o.id ? 'Enviando…' : 'Enviar datos WA'}
                          </Button>
                        ) : null}
                      </>
                    ) : null}
                    {reviewStatuses.has(o.status) && (
                      <>
                        {o.delivery_type === 'pickup' ||
                        (o.delivery_type !== 'pickup' && o.payment_method === 'cash_on_delivery') ? (
                          <Button
                            size="sm"
                            disabled={!canAcceptToKitchen(o)}
                            title={
                              !canAcceptToKitchen(o)
                                ? 'Confirma el pago antes de preparar'
                                : o.payment_method === 'cash_on_delivery' && !orderRequiresKitchen(o)
                                  ? 'Solo bebidas/complementos: confirma y atiende en caja (sin cocina)'
                                  : o.payment_method === 'cash_on_delivery'
                                    ? 'Confirma el pedido y envíalo directo a cocina'
                                    : !orderRequiresKitchen(o)
                                      ? 'Solo bebidas/complementos: confirma en caja (sin cocina)'
                                      : 'Pasa a cocina y avisa al cliente la hora de recoger'
                            }
                            onClick={() => {
                              const eta = getOrderEtaIso(o);
                              if (!eta) {
                                toast.error('Sin hora estimada');
                                return;
                              }
                              if (Date.parse(eta) < nowMexicoMs() - 60_000) {
                                toast.error('La hora ETA no puede ser anterior a la hora actual');
                                return;
                              }
                              const payload = {
                                status: 'preparing',
                                estimated_ready_at: eta,
                              };
                              if (o.delivery_type === 'pickup') {
                                payload.pickup_ready_at = eta;
                              }
                              act(o.id, payload);
                            }}
                          >
                            {o.delivery_type === 'pickup'
                              ? orderRequiresKitchen(o)
                                ? 'Confirmar pickup'
                                : 'Confirmar en caja'
                              : orderRequiresKitchen(o)
                                ? 'Confirmar y enviar a cocina'
                                : 'Confirmar en caja'}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            disabled={!canAcceptToKitchen(o)}
                            title={
                              !canAcceptToKitchen(o)
                                ? 'Confirma el pago antes de enviar a cocina'
                                : ''
                            }
                            onClick={() => {
                              const eta = getOrderEtaIso(o);
                              if (!eta) {
                                toast.error('Sin hora estimada');
                                return;
                              }
                              if (Date.parse(eta) < nowMexicoMs() - 60_000) {
                                toast.error('La hora ETA no puede ser anterior a la hora actual');
                                return;
                              }
                              act(o.id, { status: 'accepted', estimated_ready_at: eta });
                            }}
                          >
                            Aceptar Pedido
                          </Button>
                        )}
                        {o.payment_status !== 'paid' ? (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => act(o.id, { status: 'rejected', admin_notes: 'No disponible' })}
                          >
                            Rechazar
                          </Button>
                        ) : null}
                      </>
                    )}
                    {o.delivery_type !== 'pickup' && o.status === 'accepted' && (
                      <Button size="sm" onClick={() => act(o.id, { status: 'preparing' })}>
                        Preparando
                      </Button>
                    )}
                    {o.status === 'awaiting_extras' ? (
                      <Button size="sm" onClick={() => openExtrasDialog(o)}>
                        Complementos / cerrar venta
                      </Button>
                    ) : null}
                    {o.delivery_type === 'delivery' && o.status === 'ready' && (
                      <Button size="sm" onClick={() => openAssignModal(o.id)}>
                        Asignar repartidor
                      </Button>
                    )}
                    {o.delivery_type === 'delivery' && o.status === 'dispatched' && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          if (
                            !window.confirm(
                              '¿Marcar este pedido como entregado? (El repartidor no confirmó por WhatsApp)',
                            )
                          ) {
                            return;
                          }
                          act(o.id, { status: 'delivered' });
                        }}
                      >
                        Marcar entregado
                      </Button>
                    )}
                    {o.delivery_type === 'pickup' &&
                      o.status === 'ready' &&
                      o.pickup_status === 'pending_pickup' && (
                      <>
                        <Button size="sm" onClick={() => act(o.id, { pickup_status: 'picked_up' })}>
                          Marcar recogido
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => act(o.id, { pickup_status: 'not_picked_up' })}
                        >
                          No recogido
                        </Button>
                      </>
                    )}
                    {o.payment_status !== 'paid' &&
                    requiresPrepay(o) &&
                    o.payment_method === 'transfer' &&
                    !o.payment_proof_id ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => act(o.id, { payment_status: 'paid' })}
                      >
                        Marcar pagado (sin comprobante)
                      </Button>
                    ) : null}
                    {superAdmin ? (
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={deletingId === o.id}
                        onClick={() => removeOrder(o)}
                      >
                        {deletingId === o.id ? 'Eliminando…' : 'Eliminar'}
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <OrderSummaryDialog
        open={Boolean(summaryOrder)}
        order={summaryOrder}
        onOpenChange={(open) => {
          if (!open) setSummaryOrder(null);
        }}
      />

      <Dialog
        open={Boolean(extrasOrder)}
        onOpenChange={(open) => {
          if (!open) {
            setExtrasOrder(null);
            setExtrasCart([]);
          }
        }}
      >
        <DialogContent className="max-w-2xl w-[min(42rem,calc(100vw-2rem))] max-h-[85vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>
              {counterOnlySale
                ? `Caja — #${extrasOrder?.order_number}`
                : `Complementos — #${extrasOrder?.order_number}`}
            </DialogTitle>
            <DialogDescription>
              {counterOnlySale
                ? 'Confirma el pedido. Puedes agregar bebidas extra; al cerrar se registra todo y queda listo.'
                : 'Puedes quitar complementos pendientes o agregar más con precio. No se quitan productos de cocina ni lo ya registrado en caja. Cierra la venta cuando el total esté correcto.'}
            </DialogDescription>
          </DialogHeader>
          {extrasOrder ? (
            <div className="rounded-md border bg-slate-50 p-3 text-sm space-y-1">
              {counterOnlySale ? (
                <>
                  {(extrasOrder.lines || []).map((ln, idx) => (
                    <div key={`co-line-${idx}`} className="flex justify-between gap-2">
                      <span>
                        {ln.qty}x {ln.name}
                      </span>
                      <span className="tabular-nums shrink-0">
                        ${Number(ln.line_total || 0).toFixed(2)}
                      </span>
                    </div>
                  ))}
                  {extrasCart.length > 0
                    ? extrasCart.map((ln) => {
                        const it = complementCatalog
                          .flatMap((c) => c.items || [])
                          .find((x) => x.id === ln.menu_item_id);
                        const lineTotal = (Number(it?.price) || 0) * ln.qty;
                        return (
                          <div
                            key={`co-cart-${ln.menu_item_id}`}
                            className="flex justify-between gap-2 text-amber-800"
                          >
                            <span>
                              + {ln.qty}x {it?.name || 'Extra'}
                            </span>
                            <span className="tabular-nums shrink-0">+${lineTotal.toFixed(2)}</span>
                          </div>
                        );
                      })
                    : null}
                  <div className="flex justify-between border-t pt-2 font-semibold text-base">
                    <span>Total</span>
                    <span className="tabular-nums">${extrasPreviewTotal.toFixed(2)}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between">
                    <span>Total pedido (guardado)</span>
                    <span className="font-semibold tabular-nums">
                      ${Number(extrasOrder.total || 0).toFixed(2)}
                    </span>
                  </div>
                  {extrasCartSubtotal > 0 ? (
                    <div className="flex justify-between text-amber-800">
                      <span>+ Extras por agregar</span>
                      <span className="font-medium tabular-nums">+${extrasCartSubtotal.toFixed(2)}</span>
                    </div>
                  ) : null}
                  {extrasCartSubtotal > 0 ? (
                    <div className="flex justify-between border-t pt-1 font-semibold">
                      <span>Total estimado</span>
                      <span className="tabular-nums">${extrasPreviewTotal.toFixed(2)}</span>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          ) : null}
          {counterOnlySale ? (
            extrasLoading ? (
              <p className="text-sm text-muted-foreground">Cargando bebidas…</p>
            ) : (
              <div className="space-y-3 max-h-48 overflow-y-auto overflow-x-hidden">
                {complementCatalog.map((cat) => (
                  <div key={cat.id}>
                    <p className="text-sm font-semibold mb-1">{cat.name}</p>
                    <div className="flex flex-wrap gap-2">
                      {(cat.items || []).map((it) => (
                        <Button
                          key={it.id}
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={extrasSaving}
                          onClick={() => addExtraLine(it)}
                        >
                          + {it.name} (${Number(it.price).toFixed(0)})
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : null}
          {!counterOnlySale && extrasOrder && lockedOrderLines(extrasOrder).length > 0 ? (
            <div className="text-sm border rounded-md p-3 bg-slate-100 space-y-1.5">
              <p className="font-medium text-slate-800">Ya pagado / no editable</p>
              {lockedOrderLines(extrasOrder).map(({ ln, index }) => (
                <p key={`locked-${index}`} className="text-slate-700">
                  {ln.qty}x {ln.name}
                  <span className="text-muted-foreground">
                    {' '}
                    (${Number(ln.line_total || 0).toFixed(2)})
                  </span>
                  {ln.inventory_deducted ? (
                    <span className="text-xs text-emerald-700 ml-1">· registrado en caja</span>
                  ) : (
                    <span className="text-xs text-slate-500 ml-1">· cocina / pagado</span>
                  )}
                </p>
              ))}
            </div>
          ) : null}
          {!counterOnlySale && extrasOrder && removableOrderLines(extrasOrder).length > 0 ? (
            <div className="text-sm border rounded-md p-3 bg-amber-50 border-amber-200 space-y-2">
              <p className="font-medium text-amber-950">Complementos pendientes (puedes quitar o confirmar):</p>
              {removableOrderLines(extrasOrder).map(({ ln, index }) => (
                <div
                  key={`rem-${index}`}
                  className="flex items-center justify-between gap-3 min-w-0"
                >
                  <span className="min-w-0 flex-1">
                    {ln.qty}x {ln.name}
                    <span className="text-muted-foreground">
                      {' '}
                      (${Number(ln.line_total || 0).toFixed(2)})
                    </span>
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 shrink-0"
                    disabled={extrasSaving}
                    onClick={() => removeOrderLineAt(index)}
                  >
                    Quitar
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={extrasSaving || removableOrderLines(extrasOrder).length === 0}
                onClick={confirmIncludedExtras}
              >
                {extrasSaving ? '…' : 'Confirmar complementos incluidos'}
              </Button>
            </div>
          ) : null}
          {!counterOnlySale && (extrasLoading ? (
            <p className="text-sm text-muted-foreground">Cargando menú de complementos…</p>
          ) : (
            <div className="space-y-3 max-h-64 overflow-y-auto overflow-x-hidden">
              {complementCatalog.map((cat) => (
                <div key={cat.id}>
                  <p className="text-sm font-semibold mb-1">{cat.name}</p>
                  <div className="flex flex-wrap gap-2">
                    {(cat.items || []).map((it) => (
                      <Button
                        key={it.id}
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => addExtraLine(it)}
                      >
                        + {it.name} (${Number(it.price).toFixed(0)})
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
          {!counterOnlySale && extrasCart.length > 0 ? (
            <div className="text-sm border rounded-md p-2 bg-muted/30 space-y-1">
              <p className="font-medium mb-1">Extras con costo (aún no guardados):</p>
              {extrasCart.map((ln) => {
                const it = complementCatalog
                  .flatMap((c) => c.items || [])
                  .find((x) => x.id === ln.menu_item_id);
                const lineTotal = (Number(it?.price) || 0) * ln.qty;
                return (
                  <div key={ln.menu_item_id} className="flex items-center justify-between gap-2">
                    <span>
                      {ln.qty}x {it?.name || ln.menu_item_id}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        disabled={extrasSaving}
                        onClick={() => changeExtraCartQty(ln.menu_item_id, -1)}
                      >
                        <Minus className="h-3.5 w-3.5" />
                      </Button>
                      <span className="tabular-nums font-medium min-w-[4rem] text-right">
                        +${lineTotal.toFixed(2)}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        disabled={extrasSaving}
                        onClick={() => changeExtraCartQty(ln.menu_item_id, 1)}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
          <DialogFooter className="gap-2 flex-col w-full sm:flex-col sm:space-x-0">
            {counterOnlySale ? (
              <Button
                type="button"
                className="w-full whitespace-normal h-auto min-h-9 py-2"
                disabled={extrasSaving}
                onClick={() => closeCashierSale({ autoAddCart: true })}
              >
                {extrasSaving ? '…' : 'Confirmar y cerrar venta'}
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full whitespace-normal h-auto min-h-9 py-2"
                  disabled={extrasSaving || extrasCart.length === 0}
                  onClick={addExtrasToOrder}
                >
                  {extrasSaving ? 'Guardando…' : `Agregar extras al pedido${extrasCartSubtotal > 0 ? ` (+$${extrasCartSubtotal.toFixed(2)})` : ''}`}
                </Button>
                <Button
                  type="button"
                  className="w-full whitespace-normal h-auto min-h-9 py-2"
                  disabled={extrasSaving || extrasCart.length > 0}
                  onClick={closeCashierSale}
                >
                  {extrasSaving
                    ? '…'
                    : hasPendingCounterLines(extrasOrder)
                      ? 'Confirmar complementos y cerrar venta'
                      : 'Cerrar venta (marcar listo)'}
                </Button>
                {extrasCart.length > 0 ? (
                  <p className="text-xs text-muted-foreground w-full text-right">
                    Guarda o quita los extras del carrito para poder cerrar la venta.
                  </p>
                ) : null}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(linkPayment)}
        onOpenChange={(open) => {
          if (!open) {
            setLinkPayment(null);
            setLinkOrderId('');
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Vincular pago a pedido</DialogTitle>
            <DialogDescription>
              Pago MP {linkPayment?.payment_id} — ${Number(linkPayment?.amount || 0).toFixed(2)} MXN
            </DialogDescription>
          </DialogHeader>
          {pendingPaymentOrders.length === 0 ? (
            <p className="text-sm text-muted-foreground">No hay pedidos con pago pendiente hoy.</p>
          ) : (
            <div className="space-y-2">
              <Label>Pedido</Label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm"
                value={linkOrderId}
                onChange={(e) => setLinkOrderId(e.target.value)}
              >
                <option value="">Selecciona…</option>
                {pendingPaymentOrders.map((o) => (
                  <option key={o.id} value={o.id}>
                    #{o.order_number} — {o.customer_name} — ${Number(o.total).toFixed(0)}
                  </option>
                ))}
              </select>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkPayment(null)}>
              Cerrar
            </Button>
            <Button disabled={linkingPayment || !linkOrderId} onClick={submitLinkPayment}>
              {linkingPayment ? 'Vinculando…' : 'Confirmar vinculación'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(proofPreviewId)}
        onOpenChange={(open) => {
          if (!open) closeProofImage();
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Comprobante de transferencia</DialogTitle>
          </DialogHeader>
          {proofImageUrl ? (
            <img src={proofImageUrl} alt="Comprobante" className="max-h-[70vh] w-full object-contain rounded border" />
          ) : (
            <p className="text-sm text-muted-foreground">Cargando…</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeProofImage}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(assignOrderId)}
        onOpenChange={(open) => {
          if (!open) {
            setAssignOrderId(null);
            setSelectedDriverId('');
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Asignar repartidor</DialogTitle>
            <DialogDescription>
              Pedido #{assignOrder?.order_number} — se enviará por WhatsApp el detalle y la dirección al
              repartidor elegido.
            </DialogDescription>
          </DialogHeader>

          {assignOrder ? (
            <div className="rounded-md border bg-muted/40 p-3 text-sm space-y-1">
              <p>
                <span className="text-muted-foreground">Cliente:</span> {assignOrder.customer_name} ·{' '}
                {assignOrder.customer_phone}
              </p>
              <p>
                <span className="text-muted-foreground">Total:</span> ${Number(assignOrder.total).toFixed(0)}
              </p>
              <p className="text-muted-foreground">Dirección</p>
              <p className="whitespace-pre-wrap break-words">{assignOrder.delivery_address}</p>
              {orderMapsUrl(assignOrder) ? (
                <a
                  href={orderMapsUrl(assignOrder)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline text-xs"
                >
                  Abrir en Google Maps
                </a>
              ) : null}
            </div>
          ) : null}

          {drivers.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No hay repartidores activos.{' '}
              <Link to="/admin/repartidores" className="text-primary underline font-medium">
                Agregar repartidor
              </Link>
            </p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {drivers.map((d) => (
                <label
                  key={d.id}
                  className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${
                    selectedDriverId === d.id ? 'border-primary bg-primary/5' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="driver"
                    checked={selectedDriverId === d.id}
                    onChange={() => setSelectedDriverId(d.id)}
                  />
                  <span className="font-mono text-sm">{d.driver_code}</span>
                  <span>{d.name}</span>
                  <span className="text-xs text-muted-foreground ml-auto">{d.phone}</span>
                </label>
              ))}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setAssignOrderId(null)}>
              Cancelar
            </Button>
            <Button onClick={confirmAssignDriver} disabled={assigning || !selectedDriverId || drivers.length === 0}>
              {assigning ? 'Enviando…' : 'Confirmar y enviar por WhatsApp'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
